<!--
  Pagou a taxa de implantação por PIX ou transferência? O franqueado manda o comprovante aqui
  (chamado New Shoes 2026-0079, F3): valor, data e o arquivo. Mostra o último comprovante — em
  análise, ou recusado com o motivo — e some quando a taxa está quitada.
-->
<template>
  <div v-if="situacao && situacao.setup_status === 'owed'" class="comprovante" :data-testid="`setup-comprovante-${franquiaId}`">
    <p v-if="ultimo && ultimo.situacao === 'em_analise'" class="estado estado--analise" data-testid="setup-comprovante-em-analise">
      <i class="pi pi-clock" aria-hidden="true" />
      {{ t('Comprovante em análise') }}: {{ valor(ultimo) }} · {{ t('pago em') }} {{ data(ultimo.pago_em) }}
    </p>
    <p v-else-if="ultimo && ultimo.situacao === 'recusado'" class="estado estado--recusado" data-testid="setup-comprovante-recusado">
      <i class="pi pi-times-circle" aria-hidden="true" />
      {{ t('Comprovante recusado') }}: {{ ultimo.motivo }}
    </p>
    <p v-if="situacao.bloqueio === 'cof_menos_de_10_dias'" class="estado" data-testid="setup-comprovante-cof">
      {{ t('A taxa só pode ser paga 10 dias depois da entrega da COF (Lei 13.966/2019).') }}
    </p>

    <template v-if="!situacao.bloqueio">
      <app-button v-if="!aberto" :label="t('Paguei por PIX ou transferência')" icon="pi pi-file-arrow-up" size="small" outlined class="abrir"
                  data-testid="setup-comprovante-abrir" @click="aberto = true" />
      <form v-else class="formulario" data-testid="setup-comprovante-form" @submit.prevent="enviarComprovante">
        <div class="campos">
          <input-text v-model="valorPago" elem-id="setup-comprovante-valor" data-testid="setup-comprovante-valor" :label="`${t('Valor pago')} (${situacao.moeda})`" />
          <input-date v-model="pagoEm" elem-id="setup-comprovante-data" data-testid="setup-comprovante-data" :label="t('Data do pagamento')" />
        </div>
        <label class="arquivo">
          <i class="pi pi-paperclip" aria-hidden="true" /> {{ arquivo ? arquivo.name : t('Escolher o comprovante (PDF ou imagem)') }}
          <file-model :aria-label="t('Comprovante')" accept="application/pdf,image/jpeg,image/png,image/webp" data-testid="setup-comprovante-arquivo"
                      @input="arquivo = $event ?? null" />
        </label>
        <div class="botoes">
          <app-button type="submit" :label="t('Enviar comprovante')" icon="pi pi-send" size="small" :loading="enviando"
                      :disabled="!valorPago.trim() || !pagoEm || !arquivo" data-testid="setup-comprovante-enviar" />
          <app-button type="button" :label="t('Cancelar')" size="small" text @click="aberto = false" />
        </div>
      </form>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from '../../inputs/InputText.vue';
import InputDate from '../../inputs/InputDate.vue';
import FileModel from '../../inputs/FileModel.vue';
import { useSetupComprovantes, mensagemDoComprovante } from '../../composables/useSetupComprovantes';
import { valorDoComprovante, dataDoComprovante } from './comprovanteFormato';
import { eventBus } from '../../eventBus';
import type { SetupFee_ComprovanteRow } from '../../contracts/SetupFee_ComprovanteRow';

const props = defineProps<{ franquiaId: number }>();
const emit = defineEmits<{ enviado: [] }>();
const { t, n } = useI18n();
const { situacao, carregar, enviar } = useSetupComprovantes();

const aberto = ref(false);
const valorPago = ref('');
const pagoEm = ref('');
const arquivo = ref<File | null>(null);
const enviando = ref(false);

const ultimo = computed(() => situacao.value?.comprovantes?.[0] ?? null);
const valor = (c: SetupFee_ComprovanteRow): string => valorDoComprovante(c, n);
const data = dataDoComprovante;

const enviarComprovante = async (): Promise<void> => {
  if (!arquivo.value) return;
  enviando.value = true;
  try {
    await enviar(props.franquiaId, valorPago.value, pagoEm.value, arquivo.value);
    aberto.value = false;
    arquivo.value = null;
    eventBus.emit('globalMessage.success', t('Comprovante enviado para a franqueadora'));
    emit('enviado');
  } catch (e) {
    eventBus.emit('globalMessage.error', t(mensagemDoComprovante(e)));
  } finally {
    enviando.value = false;
  }
};

onMounted(() => {
  carregar(props.franquiaId).catch((e: unknown) => eventBus.emit('globalMessage.error', t(mensagemDoComprovante(e))));
});
</script>

<style scoped>
.comprovante { display: flex; flex-direction: column; gap: 10px; flex: 1 1 100%; }
.estado { margin: 0; display: flex; align-items: center; gap: 8px; font-size: 14px; color: #5b6168; }
.estado--analise { color: #1a3e9e; font-weight: 600; }
.estado--recusado { color: #8c1d13; font-weight: 600; }
.abrir { align-self: flex-start; }
.formulario { display: flex; flex-direction: column; gap: 12px; padding: 14px; border-radius: 12px; background: #fff; border: 1px solid #edebe5; max-width: 560px; }
.campos { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; }
.arquivo { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 8px; font-weight: 600; font-size: 14px; }
.botoes { display: flex; flex-wrap: wrap; gap: 8px; }
</style>
